import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, InputNumber, Space, Typography, Alert, message } from 'antd';
import { getMqttMappingByMeter, mqttPublishByMeter, getMqttLatestMessage } from '../api';
import {
  getAdminMeterPublishKey,
  remotePayloadUseBleDirect,
  buildFinalPayload,
  getRxAddressCandidates,
  buildMqttMessagePollOpts,
} from '../utils/yjMeterRemote';

const { Text } = Typography;

const NESTED_DIALOG_Z_INDEX = 2100;

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// —— 组帧/解析与小程序 test4「总用电量」一致：55 帧子命令 03 / c3；对应旧系统 MQTT 用户参数 0x03（×1000）——

function assertRemoteAddress12(meterKey, isBleDirect) {
  const m = String(meterKey || '').trim().toLowerCase();
  if (isBleDirect) return m;
  if (!/^[0-9a-f]{12}$/.test(m)) {
    throw new Error('远程 55 帧需 12 位十六进制地址（4G 多为 device_mac 去冒号），请检查设备档案');
  }
  return m;
}

/** test4 remoteRead55Value('03')：表号 + 55 + 02 + 03 + 00000000 */
function buildReadMeteringTotalRawNoCrc(publishKey, isBleDirect) {
  const m = assertRemoteAddress12(publishKey, isBleDirect);
  return ((isBleDirect ? '' : m) + '55' + '02' + '03' + '00000000').toLowerCase();
}

/** test4 remoteSend55Config('totalUsedPower', scaled)：表号 + 55 + 02 + c3 + 8位hex */
function buildSetMeteringTotalRawNoCrc(publishKey, displayValue, isBleDirect) {
  const m = assertRemoteAddress12(publishKey, isBleDirect);
  const scaled = Math.round(Number(displayValue) * 1000);
  if (scaled < 0 || scaled > 0xffffffff) throw new Error('数值超出范围');
  const valueHex = (scaled >>> 0).toString(16).padStart(8, '0');
  return ((isBleDirect ? '' : m) + '55' + '02' + 'c3' + valueHex).toLowerCase();
}

/** test4 remoteSaveParams */
function buildSaveParamsRawNoCrc(publishKey, isBleDirect) {
  const m = assertRemoteAddress12(publishKey, isBleDirect);
  return ((isBleDirect ? '' : m) + '55' + '02' + '7e' + '00000000').toLowerCase();
}

function parse55MeteringTotalResponse(rawMsg, addressCandidates) {
  const msg = String(rawMsg || '').trim().toLowerCase().replace(/\s+/g, '');
  if (msg.length < 26) return null;
  const callbackMeterNo = msg.substring(0, 12);
  const candidates = addressCandidates || [];
  if (candidates.length > 0 && !candidates.includes(callbackMeterNo)) return null;
  if (msg.substring(12, 14) !== '55' || msg.substring(14, 16) !== '02') return null;
  const subCmd = msg.substring(16, 18);
  if (subCmd !== '03' && subCmd !== 'c3') return null;
  const payloadNoCrc = msg.length > 2 ? msg.substring(0, msg.length - 2) : msg;
  const valueHex = payloadNoCrc.length >= 8 ? payloadNoCrc.substring(payloadNoCrc.length - 8) : '00000000';
  const valueDec = parseInt(valueHex || '0', 16) || 0;
  const valueFloat = Number((valueDec / 1000).toFixed(3));
  return { subCmd, valueHex, valueFloat };
}

function parse55SaveConfirm(rawMsg, addressCandidates) {
  const msg = String(rawMsg || '').trim().toLowerCase().replace(/\s+/g, '');
  if (msg.length < 26) return null;
  const callbackMeterNo = msg.substring(0, 12);
  const candidates = addressCandidates || [];
  if (candidates.length > 0 && !candidates.includes(callbackMeterNo)) return null;
  if (msg.substring(12, 14) !== '55' || msg.substring(14, 16) !== '02') return null;
  const subCmd = msg.substring(16, 18);
  const subDec = parseInt(subCmd, 16);
  if (subCmd === '7e' || (Number.isFinite(subDec) && (subDec & 0x3f) === 0x3e)) {
    return { subCmd };
  }
  return null;
}

function parse55C3SetConfirm(rawMsg, addressCandidates) {
  const parsed = parse55MeteringTotalResponse(rawMsg, addressCandidates);
  if (parsed && parsed.subCmd === 'c3') return parsed;
  return null;
}

const POLL_MS_DEFAULT = 10000;

/**
 * 4G YJ 表「计量总额」：与 test4 读取总用电量 / 设置 / 保存相同 MQTT 流程（协议用户参数 0x03）。
 */
const MeterMeteringTotalModal = ({ open, device, variant, onClose }) => {
  const [readLoading, setReadLoading] = useState(false);
  const [readValue, setReadValue] = useState(null);
  const [readHint, setReadHint] = useState('');
  const [readError, setReadError] = useState('');

  const [totalInput, setTotalInput] = useState(null);
  const [setLoading, setSetLoading] = useState(false);
  const [setHint, setSetHint] = useState('');
  const [setError, setSetError] = useState('');
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveHint, setSaveHint] = useState('');
  const [saveError, setSaveError] = useState('');
  const [, setMqttFanoutTier] = useState(1);

  const isWater = variant === 'water';
  const deviceKind = isWater ? '水表' : '电表';
  const quantityUnit = isWater ? '吨' : 'kWh';

  const resetState = useCallback(() => {
    setReadLoading(false);
    setReadValue(null);
    setReadHint('');
    setReadError('');
    setTotalInput(null);
    setSetLoading(false);
    setSetHint('');
    setSetError('');
    setSaveLoading(false);
    setSaveHint('');
    setSaveError('');
    setMqttFanoutTier(1);
  }, []);

  useEffect(() => {
    if (open) resetState();
  }, [open, device?.id, resetState]);

  const publishAndPollTotalRead = async (rxTopic, candidates, timeoutMs = POLL_MS_DEFAULT, pollOpts = {}) => {
    const deadline = Date.now() + timeoutMs;
    while (Date.now() < deadline) {
      const msgRes = await getMqttLatestMessage(rxTopic, 'raw', pollOpts);
      const raw = msgRes?.data?.message ?? msgRes?.data?.hex ?? '';
      if (raw) {
        const parsed = parse55MeteringTotalResponse(String(raw), candidates);
        if (parsed) return parsed;
      }
      await sleep(400);
    }
    return null;
  };

  const pollAfterSetC3 = async (rxTopic, candidates, timeoutMs = POLL_MS_DEFAULT, pollOpts = {}) => {
    const deadline = Date.now() + timeoutMs;
    while (Date.now() < deadline) {
      const msgRes = await getMqttLatestMessage(rxTopic, 'raw', pollOpts);
      const raw = msgRes?.data?.message ?? msgRes?.data?.hex ?? '';
      if (raw) {
        const parsed = parse55C3SetConfirm(String(raw), candidates);
        if (parsed) return parsed;
      }
      await sleep(400);
    }
    return null;
  };

  const pollSaveConfirm = async (rxTopic, candidates, timeoutMs = POLL_MS_DEFAULT, pollOpts = {}) => {
    const deadline = Date.now() + timeoutMs;
    while (Date.now() < deadline) {
      const msgRes = await getMqttLatestMessage(rxTopic, 'raw', pollOpts);
      const raw = msgRes?.data?.message ?? msgRes?.data?.hex ?? '';
      if (raw) {
        const info = parse55SaveConfirm(String(raw), candidates);
        if (info) return info;
      }
      await sleep(400);
    }
    return null;
  };

  const handleReadMeteringTotal = async () => {
    if (!device) return;
    setReadLoading(true);
    setReadError('');
    setReadHint('正在下发读取计量总额指令…');
    setReadValue(null);
    const publishKey = getAdminMeterPublishKey(device);
    if (!publishKey) {
      setReadLoading(false);
      setReadError('无法确定表号或 MAC，请检查设备档案');
      return;
    }

    const readCore = async (tier) => {
      const mapRes = await getMqttMappingByMeter(publishKey, { mapping_fanout_tier: tier });
      if (!mapRes?.success || !mapRes?.data?.rx_topic) {
        throw new Error(mapRes?.msg || mapRes?.error || '未找到该设备的 MQTT 主题映射');
      }
      const rxTopic = mapRes.data.rx_topic;
      const isBleDirect = remotePayloadUseBleDirect(publishKey);
      const rawNoCrc = buildReadMeteringTotalRawNoCrc(publishKey, isBleDirect);
      const payload = buildFinalPayload(rawNoCrc, true);
      const publishAt = Date.now();
      const pubRes = await mqttPublishByMeter({
        meter_no: publishKey,
        payload,
        qos: 1,
        mapping_fanout_tier: tier,
      });
      if (!pubRes?.success) {
        throw new Error(pubRes?.msg || pubRes?.error || '指令发送失败');
      }
      setReadHint('已下发，等待设备回调（最多约 10 秒）…');
      const candidates = getRxAddressCandidates(device);
      const pollOpts = buildMqttMessagePollOpts(publishKey, 'param55', publishAt, tier);
      return publishAndPollTotalRead(rxTopic, candidates, POLL_MS_DEFAULT, pollOpts);
    };

    try {
      let parsed = await readCore(1);
      if (!parsed) {
        setReadError('');
        setReadHint('读取超时：约 10 秒内未收到有效 55 回调。请留意上层确认框。');
        setReadLoading(false);
        Modal.confirm({
          zIndex: NESTED_DIALOG_Z_INDEX,
          title: '读取超时',
          content: '是否在更多候选路径上重试一次？',
          okText: '重试',
          cancelText: '取消',
          onOk: async () => {
            setMqttFanoutTier(2);
            setReadLoading(true);
            setReadError('');
            setReadHint('重试下发中…');
            try {
              const p2 = await readCore(2);
              if (!p2) {
                setReadHint('');
                setReadError('读取失败：重试后仍未收到有效 55 回调（子命令须为 03 或 c3）');
                return;
              }
              setReadValue(p2.valueFloat);
              setReadHint('已收到表计回调');
              setMqttFanoutTier(1);
              message.success(`当前计量总额：${p2.valueFloat} ${quantityUnit}`);
            } catch (e2) {
              const errMsg = e2?.response?.data?.msg || e2?.response?.data?.error || e2?.message || '读取失败';
              setReadHint('');
              setReadError(errMsg);
            } finally {
              setReadLoading(false);
            }
          },
        });
        return;
      }
      setReadValue(parsed.valueFloat);
      setReadHint('已收到表计回调');
      setMqttFanoutTier(1);
      message.success(`当前计量总额：${parsed.valueFloat} ${quantityUnit}`);
    } catch (e) {
      const errMsg = e?.response?.data?.msg || e?.response?.data?.error || e?.message || '读取失败';
      setReadHint('');
      setReadError(errMsg);
    } finally {
      setReadLoading(false);
    }
  };

  const handleSetMeteringTotal = async () => {
    if (!device) return;
    const v = Number(totalInput);
    if (totalInput == null || Number.isNaN(v) || v < 0) {
      message.warning(`请输入有效非负数值（内部按 ×1000 取整下发，单位：${quantityUnit}）`);
      return;
    }
    setSetLoading(true);
    setSetError('');
    setSetHint('');
    const publishKey = getAdminMeterPublishKey(device);
    if (!publishKey) {
      setSetLoading(false);
      Modal.error({
        zIndex: NESTED_DIALOG_Z_INDEX,
        title: '设置计量总额失败',
        content: '无法确定表号或 MAC，请检查设备档案',
      });
      return;
    }

    const setCore = async (tier) => {
      const mapRes = await getMqttMappingByMeter(publishKey, { mapping_fanout_tier: tier });
      if (!mapRes?.success || !mapRes?.data?.rx_topic) {
        throw new Error(mapRes?.msg || mapRes?.error || '未找到该设备的 MQTT 主题映射');
      }
      const rxTopic = mapRes.data.rx_topic;
      const candidates = getRxAddressCandidates(device);
      const isBleDirect = remotePayloadUseBleDirect(publishKey);
      const rawNoCrc = buildSetMeteringTotalRawNoCrc(publishKey, v, isBleDirect);
      const payload = buildFinalPayload(rawNoCrc, true);
      const publishAt = Date.now();
      const pubRes = await mqttPublishByMeter({
        meter_no: publishKey,
        payload,
        qos: 1,
        mapping_fanout_tier: tier,
      });
      if (!pubRes?.success) {
        throw new Error(pubRes?.msg || pubRes?.error || '指令发送失败');
      }
      setSetHint('已下发设置指令，等待设备 c3 回调（最多约 10 秒）…');
      const pollOpts = buildMqttMessagePollOpts(publishKey, 'param55', publishAt, tier);
      return pollAfterSetC3(rxTopic, candidates, POLL_MS_DEFAULT, pollOpts);
    };

    const applySetEcho = (echoed) => {
      setReadValue(echoed.valueFloat);
      setSetHint(
        `设备已回包确认（子命令 c3），解析计量总额 ${echoed.valueFloat} ${quantityUnit}。请再点「保存参数」写入表内存储。`
      );
      setMqttFanoutTier(1);
      message.success(`设置已得到表计 c3 回调：${echoed.valueFloat}`);
    };

    try {
      let echoed = await setCore(1);
      if (!echoed) {
        setSetError('');
        setSetHint('等待 c3 超时。请留意上层确认框。');
        setSetLoading(false);
        Modal.confirm({
          zIndex: NESTED_DIALOG_Z_INDEX,
          title: '未收到 c3 回包',
          content: '是否在更多候选路径上重试一次下发与轮询？',
          okText: '重试',
          cancelText: '取消',
          onOk: async () => {
            setMqttFanoutTier(2);
            setSetLoading(true);
            setSetError('');
            try {
              const e2 = await setCore(2);
              if (!e2) {
                setSetError(
                  '指令已下发，但重试后仍未在超时内收到 c3 回调。请稍后点「读取计量总额」核对；若仍不符请检查表是否在线。'
                );
                message.warning('未收到 c3 确认，请用「读取计量总额」核对');
                return;
              }
              applySetEcho(e2);
            } catch (ex) {
              const errMsg = ex?.response?.data?.msg || ex?.response?.data?.error || ex?.message || '设置失败';
              setSetHint('');
              setSetError(errMsg);
              Modal.error({
                zIndex: NESTED_DIALOG_Z_INDEX,
                title: '设置计量总额失败',
                content: errMsg,
              });
            } finally {
              setSetLoading(false);
            }
          },
        });
        return;
      }
      applySetEcho(echoed);
    } catch (e) {
      const errMsg = e?.response?.data?.msg || e?.response?.data?.error || e?.message || '设置失败';
      setSetHint('');
      setSetError(errMsg);
      Modal.error({
        zIndex: NESTED_DIALOG_Z_INDEX,
        title: '设置计量总额失败',
        content: errMsg,
      });
    } finally {
      setSetLoading(false);
    }
  };

  const handleSaveParams = async () => {
    if (!device) return;
    setSaveLoading(true);
    setSaveError('');
    setSaveHint('');
    const publishKey = getAdminMeterPublishKey(device);
    if (!publishKey) {
      setSaveLoading(false);
      Modal.error({
        zIndex: NESTED_DIALOG_Z_INDEX,
        title: '保存参数失败',
        content: '无法确定表号或 MAC，请检查设备档案',
      });
      return;
    }

    const saveCore = async (tier) => {
      const mapRes = await getMqttMappingByMeter(publishKey, { mapping_fanout_tier: tier });
      if (!mapRes?.success || !mapRes?.data?.rx_topic) {
        throw new Error(mapRes?.msg || mapRes?.error || '未找到该设备的 MQTT 主题映射');
      }
      const rxTopic = mapRes.data.rx_topic;
      const isBleDirect = remotePayloadUseBleDirect(publishKey);
      const rawNoCrc = buildSaveParamsRawNoCrc(publishKey, isBleDirect);
      const payload = buildFinalPayload(rawNoCrc, true);
      const publishAt = Date.now();
      const pubRes = await mqttPublishByMeter({
        meter_no: publishKey,
        payload,
        qos: 1,
        mapping_fanout_tier: tier,
      });
      if (!pubRes?.success) {
        throw new Error(pubRes?.msg || pubRes?.error || '保存指令发送失败');
      }
      const candidates = getRxAddressCandidates(device);
      const pollOpts = buildMqttMessagePollOpts(publishKey, 'param55', publishAt, tier);
      setSaveHint('已下发保存指令，等待 7e 类确认（最多约 10 秒）…');
      return pollSaveConfirm(rxTopic, candidates, POLL_MS_DEFAULT, pollOpts);
    };

    try {
      let saveInfo = await saveCore(1);
      if (!saveInfo) {
        setSaveError('');
        setSaveHint('等待保存确认超时。请留意上层确认框。');
        setSaveLoading(false);
        Modal.confirm({
          zIndex: NESTED_DIALOG_Z_INDEX,
          title: '未收到保存确认',
          content: '是否在更多候选路径上重试一次？',
          okText: '重试',
          cancelText: '取消',
          onOk: async () => {
            setMqttFanoutTier(2);
            setSaveLoading(true);
            setSaveError('');
            try {
              const s2 = await saveCore(2);
              if (!s2) {
                setSaveError(
                  '保存指令已下发，但重试后仍未在超时内收到 7e 类确认。请稍后点「读取计量总额」核对是否已写入。'
                );
                message.warning('未收到保存确认，请用「读取计量总额」核对');
                return;
              }
              setSaveHint(`设备已回包确认保存（子命令 ${s2.subCmd}）。建议再点「读取计量总额」确认落盘值。`);
              setMqttFanoutTier(1);
              message.success('保存参数已得到表计确认');
            } catch (ex) {
              const errMsg = ex?.response?.data?.msg || ex?.response?.data?.error || ex?.message || '保存失败';
              setSaveHint('');
              setSaveError(errMsg);
              Modal.error({
                zIndex: NESTED_DIALOG_Z_INDEX,
                title: '保存参数失败',
                content: errMsg,
              });
            } finally {
              setSaveLoading(false);
            }
          },
        });
        return;
      }
      setSaveHint(`设备已回包确认保存（子命令 ${saveInfo.subCmd}）。建议再点「读取计量总额」确认落盘值。`);
      setMqttFanoutTier(1);
      message.success('保存参数已得到表计确认');
    } catch (e) {
      const errMsg = e?.response?.data?.msg || e?.response?.data?.error || e?.message || '保存失败';
      setSaveHint('');
      setSaveError(errMsg);
      Modal.error({
        zIndex: NESTED_DIALOG_Z_INDEX,
        title: '保存参数失败',
        content: errMsg,
      });
    } finally {
      setSaveLoading(false);
    }
  };

  return (
    <Modal
      title={`计量总额 — ${device?.current_meter_no || device?.id || ''}（${deviceKind}）`}
      open={open}
      onCancel={onClose}
      footer={[
        <Button key="close" onClick={onClose}>
          关闭
        </Button>,
      ]}
      width={520}
      zIndex={1000}
      destroyOnClose
      maskClosable={false}
    >
      {device && (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Alert
            type="info"
            showIcon
            message="与小程序 test4「总用电量」及旧系统用户参数 0x03 一致"
            description={
              <span>
                读取：<Text code>55 02 03 00000000</Text>；设置：<Text code>55 02 c3</Text> + 数值×1000 的 8
                位十六进制；保存：<Text code>55 02 7e 00000000</Text>。回调值 = 报文末 8 位 hex 整数 ÷ 1000（显示单位：
                {quantityUnit}）。
              </span>
            }
          />

          {readError && <Alert type="error" message={readError} showIcon />}
          {readHint && !readError && (
            <Alert type={readHint.includes('已收到') ? 'success' : 'info'} message={readHint} showIcon />
          )}

          <div>
            <Text strong>读取计量总额</Text>
            <div style={{ marginTop: 8 }}>
              <Text type="secondary">表计回调解析值（÷1000，{quantityUnit}）</Text>
              <div style={{ fontSize: 20, fontWeight: 600, marginTop: 6 }}>
                {readValue != null && !Number.isNaN(readValue) ? readValue.toFixed(3) : '—'}
              </div>
            </div>
            <Button type="primary" style={{ marginTop: 10 }} loading={readLoading} onClick={handleReadMeteringTotal}>
              读取计量总额
            </Button>
          </div>

          <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 12 }}>
            <Text strong>设置计量总额</Text>
            <div style={{ marginTop: 8 }}>
              <Space wrap align="center">
                <span>总额（{quantityUnit}）</span>
                <InputNumber
                  min={0}
                  step={0.001}
                  precision={3}
                  value={totalInput}
                  onChange={setTotalInput}
                  placeholder={isWater ? '如 12.345' : '如 100.000'}
                  style={{ width: 160 }}
                />
                <Button type="primary" loading={setLoading} onClick={handleSetMeteringTotal}>
                  设置计量总额
                </Button>
              </Space>
            </div>
            {setError && (
              <Alert type="warning" message={setError} showIcon style={{ marginTop: 10 }} />
            )}
            {setHint && !setError && (
              <Alert
                type={setHint.includes('已回包确认') ? 'success' : 'info'}
                message={setHint}
                showIcon
                style={{ marginTop: 10 }}
              />
            )}
            <Text type="secondary" style={{ display: 'block', marginTop: 10, fontSize: 12 }}>
              设置后等待 c3 回包；保存参数后建议再「读取计量总额」确认落盘。
            </Text>
            <div style={{ marginTop: 12 }}>
              <Text strong>保存参数</Text>
            </div>
            {saveError && (
              <Alert type="warning" message={saveError} showIcon style={{ marginTop: 8 }} />
            )}
            {saveHint && !saveError && (
              <Alert
                type={saveHint.includes('已回包确认') ? 'success' : 'info'}
                message={saveHint}
                showIcon
                style={{ marginTop: 8 }}
              />
            )}
            <Button style={{ marginTop: 8 }} loading={saveLoading} onClick={handleSaveParams}>
              保存参数
            </Button>
          </div>
        </Space>
      )}
    </Modal>
  );
};

export default MeterMeteringTotalModal;
